import { useQuery, useMutation, useQueryClient } from 'react-query';
import { predictionManagerAxiosApi } from 'src/Config/axiosApi';
import type {
  SniperGroup,
  SniperGroupMode,
  CreateSniperGroupDto,
  UpsertSniperEventDto,
  ImportSniperMarketsDto,
  SniperMarketCandidate,
  SniperMarketDraft,
  SniperMarket,
  PatchSniperMarketDto,
} from '../types/sniper.types';

/**
 * Control-plane REST for the Sniper page (prediction-manager, base path
 * `/sniper/*` — see `sniper-live-event-directional-sweep.md` §Control-plane
 * REST). Re-uses the shared `predictionManagerAxiosApi` (Bearer + 401-refresh
 * interceptors), mirroring `useMatchEvents.ts` / `useGroups.ts`: a GET list
 * query + CRUD mutations that invalidate the list.
 *
 * No frontend fallback — hooks render exactly what the backend returns.
 */

const GROUPS_KEY = 'sniper-groups';

// ─── groups + nested events + markets ────────────────────────────────────────

/** `GET /sniper/groups` — groups with nested events (+ their markets). */
export function useSniperGroups() {
  return useQuery<SniperGroup[]>(
    [GROUPS_KEY],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<
        SniperGroup[] | { groups: SniperGroup[] }
      >('/sniper/groups');
      return Array.isArray(data) ? data : (data?.groups ?? []);
    },
    { refetchInterval: 15_000, keepPreviousData: true },
  );
}

/** `POST /sniper/groups` — create a group. */
export function useCreateSniperGroup() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: CreateSniperGroupDto) => {
      const { data } = await predictionManagerAxiosApi.post<SniperGroup>(
        '/sniper/groups',
        dto,
      );
      return data;
    },
    { onSuccess: () => qc.invalidateQueries([GROUPS_KEY]) },
  );
}

/** `DELETE /sniper/groups/:id` — delete group + cascade-delete its markets. */
export function useDeleteSniperGroup() {
  const qc = useQueryClient();
  return useMutation(
    async (groupId: string) => {
      const { data } = await predictionManagerAxiosApi.delete(
        `/sniper/groups/${groupId}`,
      );
      return data;
    },
    { onSuccess: () => qc.invalidateQueries([GROUPS_KEY]) },
  );
}

/** `POST /sniper/groups/:id/events` — upsert activatable_match + link to group. */
export function useUpsertSniperEvent() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      groupId,
      dto,
    }: {
      groupId: string;
      dto: UpsertSniperEventDto;
    }) => {
      const { data } = await predictionManagerAxiosApi.post(
        `/sniper/groups/${groupId}/events`,
        dto,
      );
      return data;
    },
    { onSuccess: () => qc.invalidateQueries([GROUPS_KEY]) },
  );
}

// ─── import-by-link (scrape → candidates, NO persist) ────────────────────────

/**
 * `POST /sniper/events/:fixtureId/import` — scrape a Kalshi and/or Polymarket
 * event URL and return candidate markets (with auto-guessed counter/line) for
 * operator review. Does NOT persist rows. A mutation, not a query — the
 * operator triggers it explicitly and reviews the result.
 */
export function useImportSniperMarkets() {
  return useMutation(
    async ({
      fixtureId,
      dto,
    }: {
      fixtureId: number;
      dto: ImportSniperMarketsDto;
    }) => {
      const { data } = await predictionManagerAxiosApi.post<
        SniperMarketCandidate[] | { candidates: SniperMarketCandidate[] }
      >(`/sniper/events/${fixtureId}/import`, dto);
      return Array.isArray(data) ? data : (data?.candidates ?? []);
    },
  );
}

/** `POST /sniper/events/:fixtureId/markets` — persist reviewed markets. */
export function useCreateSniperMarkets() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      fixtureId,
      markets,
    }: {
      fixtureId: number;
      markets: SniperMarketDraft[];
    }) => {
      const { data } = await predictionManagerAxiosApi.post<SniperMarket[]>(
        `/sniper/events/${fixtureId}/markets`,
        { markets },
      );
      return data;
    },
    { onSuccess: () => qc.invalidateQueries([GROUPS_KEY]) },
  );
}

/** `PATCH /sniper/markets/:id` — update one market row + rewrite config. */
export function usePatchSniperMarket() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      marketId,
      patch,
    }: {
      marketId: string;
      patch: PatchSniperMarketDto;
    }) => {
      const { data } = await predictionManagerAxiosApi.patch<SniperMarket>(
        `/sniper/markets/${marketId}`,
        patch,
      );
      return data;
    },
    { onSuccess: () => qc.invalidateQueries([GROUPS_KEY]) },
  );
}

/** `DELETE /sniper/markets/:id` — remove a market row + rewrite config. */
export function useDeleteSniperMarket() {
  const qc = useQueryClient();
  return useMutation(
    async (marketId: string) => {
      const { data } = await predictionManagerAxiosApi.delete(
        `/sniper/markets/${marketId}`,
      );
      return data;
    },
    { onSuccess: () => qc.invalidateQueries([GROUPS_KEY]) },
  );
}

// ─── event activate / deactivate (reuses /match-events feed active set) ───────

/** `POST /sniper/events/:fixtureId/activate`. */
export function useActivateSniperEvent() {
  const qc = useQueryClient();
  return useMutation(
    async (fixtureId: number) => {
      const { data } = await predictionManagerAxiosApi.post(
        `/sniper/events/${fixtureId}/activate`,
      );
      return data;
    },
    { onSuccess: () => qc.invalidateQueries([GROUPS_KEY]) },
  );
}

/** `POST /sniper/events/:fixtureId/deactivate`. */
export function useDeactivateSniperEvent() {
  const qc = useQueryClient();
  return useMutation(
    async (fixtureId: number) => {
      const { data } = await predictionManagerAxiosApi.post(
        `/sniper/events/${fixtureId}/deactivate`,
      );
      return data;
    },
    { onSuccess: () => qc.invalidateQueries([GROUPS_KEY]) },
  );
}

// ─── per-group run state (start/stop) + panic + halt (backend PR #200) ────────

/** `POST /sniper/groups/:id/start {mode}` — start a group in test|normal mode. */
export function useStartGroup() {
  const qc = useQueryClient();
  return useMutation(
    async ({ groupId, mode }: { groupId: string; mode: SniperGroupMode }) => {
      const { data } = await predictionManagerAxiosApi.post(
        `/sniper/groups/${groupId}/start`,
        { mode },
      );
      return data;
    },
    { onSuccess: () => qc.invalidateQueries([GROUPS_KEY]) },
  );
}

/** `POST /sniper/groups/:id/stop` — stop a running group. */
export function useStopGroup() {
  const qc = useQueryClient();
  return useMutation(
    async (groupId: string) => {
      const { data } = await predictionManagerAxiosApi.post(
        `/sniper/groups/${groupId}/stop`,
      );
      return data;
    },
    { onSuccess: () => qc.invalidateQueries([GROUPS_KEY]) },
  );
}

/** `POST /sniper/groups/:id/panic` — immediate group-scoped panic (halt). */
export function usePanicGroup() {
  const qc = useQueryClient();
  return useMutation(
    async (groupId: string) => {
      const { data } = await predictionManagerAxiosApi.post(
        `/sniper/groups/${groupId}/panic`,
      );
      return data;
    },
    { onSuccess: () => qc.invalidateQueries([GROUPS_KEY]) },
  );
}

/** `POST /sniper/panic` — GLOBAL panic across every Sniper group. */
export function usePanicAll() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const { data } = await predictionManagerAxiosApi.post('/sniper/panic');
      return data;
    },
    { onSuccess: () => qc.invalidateQueries([GROUPS_KEY]) },
  );
}

/**
 * `DELETE /sniper/groups/:id/halt` — clear a group's halt. Does NOT resume:
 * the group stays stopped until it is Started again.
 */
export function useClearGroupHalt() {
  const qc = useQueryClient();
  return useMutation(
    async (groupId: string) => {
      const { data } = await predictionManagerAxiosApi.delete(
        `/sniper/groups/${groupId}/halt`,
      );
      return data;
    },
    { onSuccess: () => qc.invalidateQueries([GROUPS_KEY]) },
  );
}

/**
 * `DELETE /sniper/halt` — clear the GLOBAL halt. Does NOT resume any group;
 * each stays stopped until Started.
 */
export function useClearGlobalHalt() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const { data } = await predictionManagerAxiosApi.delete('/sniper/halt');
      return data;
    },
    { onSuccess: () => qc.invalidateQueries([GROUPS_KEY]) },
  );
}
